/**
 * io-product-scroll.js
 * Arrow scrolling + custom scrollbar sync for horizontal product rows.
 *
 * Instances are found by either hook:
 *   [data-section-type="product-scroll"]  → Scrollable Product Row section
 *   [data-product-scroll]                 → any row inside another section
 *                                           (e.g. IO Youth Polo Collection,
 *                                           which has two independent rows)
 *
 * Each matching element is its own instance, so one section can contain
 * several tracks without them fighting over the same controls.
 */

(function () {
  var SELECTOR = '[data-section-type="product-scroll"], [data-product-scroll]';

  function initProductScroll(root) {
    if (!root || root.dataset.scrollInit === 'true') return;

    var track = root.querySelector('.product-row__track');
    if (!track) return;

    root.dataset.scrollInit = 'true';

    var controls       = root.querySelector('.product-controls');
    var thumb          = root.querySelector('.custom-scrollbar-thumb');
    var scrollbarTrack = root.querySelector('.custom-scrollbar-track');
    var btnLeft        = root.querySelector('.scroll-btn.left');
    var btnRight       = root.querySelector('.scroll-btn.right');

    var isScrolling = false;
    var scrollTimer = null;

    function maxScroll() {
      return track.scrollWidth - track.clientWidth;
    }

    function isScrollable() {
      return maxScroll() > 1;
    }

    // Works for both card markups: .product-card (standalone section)
    // and .grid__item (Impulse product-grid-item snippet).
    function getCard() {
      return track.querySelector('.product-card, .grid__item') || track.firstElementChild;
    }

    // Read the real gap so changing --io-gap doesn't desync the arrows.
    function getGap() {
      var gap = parseFloat(getComputedStyle(track).columnGap);
      return isNaN(gap) ? 16 : gap;
    }

    // Jump by however many whole cards currently fit in view (min 1).
    function getScrollStep() {
      var card = getCard();
      if (!card) return 280;
      var cardWidth = card.getBoundingClientRect().width + getGap();
      var perView   = Math.max(1, Math.floor(track.clientWidth / cardWidth));
      return cardWidth * perView;
    }

    /*
     * scrollBy is asynchronous — scrollLeft hasn't settled when a second
     * click fires, so a stale value could carry the track past either end.
     * Fix: clamp an absolute target with scrollTo, and block clicks until
     * the animation reports done (scrollend, or a 400ms fallback).
     */
    function scrollToPosition(targetLeft) {
      var clamped = Math.max(0, Math.min(targetLeft, maxScroll()));
      if (Math.abs(track.scrollLeft - clamped) < 2) return;

      isScrolling = true;
      track.scrollTo({ left: clamped, behavior: 'smooth' });

      clearTimeout(scrollTimer);

      if ('onscrollend' in track) {
        track.addEventListener('scrollend', function onEnd() {
          track.removeEventListener('scrollend', onEnd);
          isScrolling = false;
          update();
        });
      } else {
        // Safari / older browsers
        scrollTimer = setTimeout(function () {
          isScrolling = false;
          update();
        }, 400);
      }
    }

    // ----- Arrow buttons -----
    if (btnLeft) {
      btnLeft.addEventListener('click', function () {
        if (isScrolling) return;
        scrollToPosition(track.scrollLeft - getScrollStep());
      });
    }

    if (btnRight) {
      btnRight.addEventListener('click', function () {
        if (isScrolling) return;
        scrollToPosition(track.scrollLeft + getScrollStep());
      });
    }

    // ----- Custom scrollbar thumb -----
    function updateThumb() {
      if (!thumb || !scrollbarTrack) return;

      var scrollable = maxScroll();
      var trackWidth = scrollbarTrack.offsetWidth;

      if (scrollable <= 0 || trackWidth <= 0) {
        thumb.style.width = '100%';
        thumb.style.left  = '0px';
        return;
      }

      var progress   = track.scrollLeft / scrollable;
      var thumbWidth = Math.max(40, (track.clientWidth / track.scrollWidth) * trackWidth);

      thumb.style.width = thumbWidth + 'px';
      thumb.style.left  = (progress * (trackWidth - thumbWidth)) + 'px';
    }

    // ----- Button disabled state -----
    function updateButtons() {
      if (!btnLeft || !btnRight) return;
      btnLeft.disabled  = track.scrollLeft <= 0;
      btnRight.disabled = track.scrollLeft >= maxScroll() - 1;
    }

    // ----- Hide the whole control bar when there's nothing to scroll -----
    function updateControls() {
      if (!controls) return;
      controls.style.display = isScrollable() ? '' : 'none';
    }

    function update() {
      updateThumb();
      updateButtons();
      updateControls();
    }

    track.addEventListener('scroll', update, { passive: true });

    // ----- Draggable thumb (pointer events cover mouse + touch + pen) -----
    if (thumb && scrollbarTrack) {
      var dragging  = false;
      var startX    = 0;
      var startLeft = 0;

      thumb.addEventListener('pointerdown', function (e) {
        dragging  = true;
        startX    = e.clientX;
        startLeft = track.scrollLeft;
        if (thumb.setPointerCapture) thumb.setPointerCapture(e.pointerId);
        e.preventDefault();
      });

      thumb.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        var trackWidth = scrollbarTrack.offsetWidth;
        var thumbWidth = thumb.offsetWidth;
        var travel     = trackWidth - thumbWidth;
        if (travel <= 0) return;

        var ratio = (e.clientX - startX) / travel;
        track.scrollLeft = startLeft + ratio * maxScroll();
      });

      ['pointerup', 'pointercancel'].forEach(function (evt) {
        thumb.addEventListener(evt, function () { dragging = false; });
      });
    }

    // Initial state
    update();

    // Recalculate when the track resizes, and once lazy images have loaded
    // (product images change scrollWidth after first paint).
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(update);
      ro.observe(track);
    } else {
      window.addEventListener('resize', update);
    }

    track.querySelectorAll('img').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', update, { once: true });
    });
  }

  function boot(scope) {
    var context = scope || document;
    if (context.matches && context.matches(SELECTOR)) initProductScroll(context);
    context.querySelectorAll(SELECTOR).forEach(initProductScroll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  // Shopify theme editor — re-init every row in the reloaded section
  document.addEventListener('shopify:section:load', function (e) {
    boot(e.target);
  });
})();
